import type { HelpArticle } from '../../../components/help/types';

export const searchReportsArticles: HelpArticle[] = [
  {
    id: 'search-basics',
    title: 'Searching for Documents',
    summary: 'How to find documents quickly using search.',
    category: 'search',
    order: 1,
    keywords: ['search', 'find', 'query', 'locate', 'lookup'],
    content: `
# Searching for Documents

Find any document instantly using the powerful search feature.

## Quick Search

Press \`Ctrl + K\` anywhere to open quick search:
- Type your search terms
- Results appear as you type
- Press Enter to see full results
- Use arrow keys to navigate

## Advanced Search

For detailed searching:
1. Go to **Search** in the sidebar
2. Enter your search terms
3. Use filters to narrow results

## Search Filters

Filter by:
- **Date Range**: Created or modified dates
- **Document Type**: Letter, Memo, Report, etc.
- **Classification**: Security level
- **Status**: Draft, Approved, etc.
- **Department**: Origin department
- **Author**: Document creator

## Search Tips

- Use quotes for exact phrases: \`"annual report"\`
- Combine terms: \`budget 2024 ministry\`
- Filter early to reduce results
- Save frequently used searches

## Search Results

Results show:
- Document title and type
- Matched text highlighted
- Status and classification
- Last modified date
    `,
  },
  {
    id: 'search-saved',
    title: 'Saving and Managing Searches',
    summary: 'Save frequent searches for quick access.',
    category: 'search',
    order: 2,
    keywords: ['save', 'saved', 'bookmark', 'favorite', 'quick access'],
    content: `
# Saving and Managing Searches

Save your frequently used searches for quick access.

## Saving a Search

1. Perform your search
2. Apply desired filters
3. Click **Save Search**
4. Give it a name
5. Click **Save**

## Accessing Saved Searches

- Saved searches appear in the sidebar
- Click to run instantly
- Results reflect current data

## Managing Saved Searches

### Rename
1. Click the menu icon
2. Select **Rename**
3. Enter new name
4. Click **Save**

### Delete
1. Click the menu icon
2. Select **Delete**
3. Confirm deletion

### Edit
1. Click the menu icon
2. Select **Edit**
3. Modify search criteria
4. Save changes

## Sharing Searches

Share with colleagues:
1. Open the saved search
2. Click **Share**
3. Select users or roles
4. Click **Share**
    `,
  },
  {
    id: 'reports-generating',
    title: 'Generating Reports',
    summary: 'Create and export reports from your data.',
    category: 'search',
    order: 3,
    keywords: ['report', 'generate', 'export', 'pdf', 'excel', 'analytics'],
    content: `
# Generating Reports

Create comprehensive reports from your document data.

## Available Reports

- **Document Summary**: Overview of all documents
- **Activity Report**: Actions taken over time
- **Workflow Status**: Pending and completed workflows
- **Classification Distribution**: Documents by security level
- **Department Activity**: Activity by department

## Creating a Report

1. Go to **Reports** in the sidebar
2. Select the report type
3. Set parameters:
   - Date range
   - Departments
   - Document types
4. Click **Generate**

## Export Formats

- **PDF**: Printable, formatted report
- **Excel**: Editable spreadsheet
- **CSV**: Raw data for analysis

## Scheduling Reports

Set up automatic reports:
1. Create the report
2. Click **Schedule**
3. Set frequency (daily, weekly, monthly)
4. Add email recipients
5. Activate the schedule

## Report Tips

- Use date ranges for trend analysis
- Export to Excel for custom analysis
- Schedule regular reports for stakeholders
- Filter carefully for relevant data
    `,
  },
];
